/**
 * useCablePathDrag
 *
 * Hook for managing drag operations on cable path handles.
 * Supports dragging:
 * - Start junction (endpoint)
 * - End junction (endpoint)
 * - Intermediate vertices (turning points)
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { Point } from '@/types/common';
import type { CablePath } from '@/types/cablePaths';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';

interface PathDragState {
  pathId: string;
  handleType: 'start' | 'end' | 'vertex';
  vertexIndex: number;
  initialPos: Point;
  currentPos: Point;
}

interface UseCablePathDragProps {
  containerRef: React.RefObject<HTMLDivElement>;
  zoom: number;
  position: { x: number; y: number };
  paths: CablePath[] | undefined;
  updatePathGeometry: (
    id: string,
    points: Point[],
    startJunctionId?: string | null,
    endJunctionId?: string | null,
  ) => Promise<void>;
  moveJunction: (id: string, x: number, y: number) => Promise<void>;
}

interface UseCablePathDragReturn {
  /** Current drag state, null if not dragging */
  dragState: PathDragState | null;
  /** Whether any drag is in progress */
  isDragging: boolean;
  /** Handler to call on mouse down on a handle */
  handleDragStart: (
    pathId: string,
    handleType: 'start' | 'end' | 'vertex',
    index: number,
    initialPos: Point,
    e: React.MouseEvent,
  ) => void;
  /** Get drag preview position for a specific handle */
  getDragPreviewPos: (
    pathId: string,
    handleType: 'start' | 'end' | 'vertex',
    index: number,
  ) => Point | null;
  /** Check if a specific handle is being dragged */
  isDraggingHandle: (
    pathId: string,
    handleType: 'start' | 'end' | 'vertex',
    index: number,
  ) => boolean;
}

export function useCablePathDrag({
  containerRef,
  zoom,
  position,
  paths,
  updatePathGeometry,
  moveJunction,
}: UseCablePathDragProps): UseCablePathDragReturn {
  const [dragState, setDragState] = useState<PathDragState | null>(null);
  const isDragging = dragState !== null;
  const dragStateRef = useRef(dragState);

  // Keep ref in sync with state
  useEffect(() => {
    dragStateRef.current = dragState;
  }, [dragState]);

  // Convert screen coordinates to floor plan coordinates
  const screenToFloorPlanCoords = useCallback(
    (clientX: number, clientY: number): Point | null => {
      if (!containerRef.current) return null;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (clientX - rect.left - position.x) / zoom;
      const y = (clientY - rect.top - position.y) / zoom;
      return { x, y };
    },
    [containerRef, position, zoom],
  );

  // Handle drag start
  const handleDragStart = useCallback(
    (
      pathId: string,
      handleType: 'start' | 'end' | 'vertex',
      index: number,
      initialPos: Point,
      e: React.MouseEvent,
    ) => {
      e.preventDefault();
      e.stopPropagation();

      logger.log('[useCablePathDrag] Starting path drag', {
        pathId,
        handleType,
        index,
        initialPos,
      });

      setDragState({
        pathId,
        handleType,
        vertexIndex: index,
        initialPos,
        currentPos: initialPos,
      });
    },
    [],
  );

  // Handle mouse move during drag
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragStateRef.current) return;

      const newPos = screenToFloorPlanCoords(e.clientX, e.clientY);
      if (!newPos) return;

      setDragState(prev =>
        prev
          ? {
              ...prev,
              currentPos: newPos,
            }
          : null,
      );
    },
    [screenToFloorPlanCoords],
  );

  // Handle mouse up - finalize drag
  const handleMouseUp = useCallback(async () => {
    const currentDragState = dragStateRef.current;
    if (!currentDragState) return;

    const { pathId, handleType, vertexIndex, currentPos } = currentDragState;

    // Find the path
    const path = paths?.find(p => p.id === pathId);
    if (!path) {
      logger.error('Path not found during drag end:', pathId);
      setDragState(null);
      return;
    }

    try {
      if (handleType === 'vertex') {
        // Update path points array
        const newPoints = [...path.points];
        newPoints[vertexIndex] = currentPos;

        logger.log('Updating path vertex', { pathId, vertexIndex, newPos: currentPos });
        await updatePathGeometry(pathId, newPoints, path.start_junction_id, path.end_junction_id);
      } else if (handleType === 'start' && path.start_junction_id) {
        // Move start junction
        logger.log('Moving start junction', {
          junctionId: path.start_junction_id,
          newPos: currentPos,
        });
        await moveJunction(path.start_junction_id, currentPos.x, currentPos.y);
      } else if (handleType === 'end' && path.end_junction_id) {
        // Move end junction
        logger.log('Moving end junction', { junctionId: path.end_junction_id, newPos: currentPos });
        await moveJunction(path.end_junction_id, currentPos.x, currentPos.y);
      }
    } catch (error) {
      logger.error('Failed to update path geometry:', error);
      toast.error('Failed to update path');
    }

    setDragState(null);
  }, [paths, updatePathGeometry, moveJunction]);

  // Handle escape key to cancel drag
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && dragStateRef.current) {
      logger.log('Drag cancelled');
      setDragState(null);
    }
  }, []);

  // Set up global event listeners when dragging
  useEffect(() => {
    if (!isDragging) return;

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleKeyDown]);

  // Get drag preview position for a specific handle
  const getDragPreviewPos = useCallback(
    (pathId: string, handleType: 'start' | 'end' | 'vertex', index: number): Point | null => {
      if (
        !dragState ||
        dragState.pathId !== pathId ||
        dragState.handleType !== handleType ||
        (handleType === 'vertex' && dragState.vertexIndex !== index)
      ) {
        return null;
      }
      return dragState.currentPos;
    },
    [dragState],
  );

  // Check if a specific handle is being dragged
  const isDraggingHandle = useCallback(
    (pathId: string, handleType: 'start' | 'end' | 'vertex', index: number): boolean => {
      if (!dragState) return false;
      return (
        dragState.pathId === pathId &&
        dragState.handleType === handleType &&
        (handleType !== 'vertex' || dragState.vertexIndex === index)
      );
    },
    [dragState],
  );

  return {
    dragState,
    isDragging,
    handleDragStart,
    getDragPreviewPos,
    isDraggingHandle,
  };
}
